'use strict';
const observable = require('@mindmup/mapjs-core/src/util/observable'),
	arrayWithUniqueElements = require('@mindmup/js-utils/src/array-with-unique-elements'),
	_ = require('underscore'),
	STICKERS_ATTR_NAME = 'stickers',
	toStickerDescriptor = sticker => {
		if (!sticker || !sticker.codepoint) {
			throw new Error('invalid-args');
		}
		return `emoji:${sticker.codepoint}`;
	},
	fromStickerDescriptor = (stickerDescriptor, category) => {
		const components = stickerDescriptor && typeof stickerDescriptor === 'string' && stickerDescriptor.split(':');
		if (!category || !components || components.length !== 2 || components[0] !== 'emoji') {
			throw new Error('invalid-args');
		}
		return {codepoint: components[1], category: category};
	};

module.exports = function StickersEditorModel(params) {
	let isActive = false;

	const self = observable(this),
		{mapModel, mapEditorModel, ajaxPromise, stickerList, version} = params,
		stickersForOtherCategories = {},
		loadStickersForMetadata = async () => {
			self.dispatchEvent('loading');
			try {
				const loaded = await ajaxPromise.get(`/${version}/stickers.json`);
				if (!Array.isArray(loaded)) {
					self.dispatchEvent('loading-error');
				} else {
					stickerList.load(loaded);

				}
			} catch (e) {
				self.dispatchEvent('loading-error');
			};
		},
		getStickerDescriptorsFromActivatedNodes = () => {
			const stickerDescriptors = [],
				activeNodeIds = mapModel.getActivatedNodeIds();
			activeNodeIds.forEach(nodeId => {
				const idea = mapModel.findIdeaById(nodeId),
					stickers = (idea && idea.attr && idea.attr[STICKERS_ATTR_NAME]);
				if (stickers && Array.isArray(stickers) && stickers.length) {
					stickerDescriptors.push.apply(stickerDescriptors, stickers);
				}
			});
			return arrayWithUniqueElements(stickerDescriptors);
		},
		getStickerDescriptorsForCurrentMap = () => {
			const stickerDescriptors = [],
				activeContent = mapModel.getIdea();

			activeContent.traverse(idea => {
				const stickers = (idea && idea.attr && idea.attr[STICKERS_ATTR_NAME]);
				if (stickers && Array.isArray(stickers) && stickers.length) {
					stickerDescriptors.push.apply(stickerDescriptors, stickers);
				}
			});
			return arrayWithUniqueElements(stickerDescriptors);
		},
		loadStickersForActivatedNodes = () => {
			const category = 'In activated nodes',
				newStickers = getStickerDescriptorsFromActivatedNodes().map(stickerDescriptor => fromStickerDescriptor(stickerDescriptor, category));

			if (_.isEqual(newStickers, stickersForOtherCategories[category])) {
				return;
			}
			stickersForOtherCategories[category] = newStickers;
			if (stickersForOtherCategories[category].length) {
				self.dispatchEvent('stickers-for-activated-loaded', category);
			} else {
				self.dispatchEvent('no-stickers-for-activated-loaded');
			}
		},
		loadStickersForCurrentMap = () => {
			const category = 'In this map',
				newStickers = getStickerDescriptorsForCurrentMap().map(stickerDescriptor => fromStickerDescriptor(stickerDescriptor, category));

			if (_.isEqual(newStickers, stickersForOtherCategories[category])) {
				return;
			}
			stickersForOtherCategories[category] = newStickers;
			if (stickersForOtherCategories[category].length) {
				self.dispatchEvent('stickers-for-map-loaded', category);
			} else {
				self.dispatchEvent('no-stickers-for-map-loaded');
			}
		},
		loadStickersForOtherCategories = () => {
			loadStickersForActivatedNodes();
			loadStickersForCurrentMap();
		};
	if (!version || !mapModel || !mapEditorModel || !ajaxPromise || !stickerList) {
		throw new Error('invalid-args');
	}
	self.setActive = async active => {
		if (isActive === !!active) {
			return;
		}
		isActive = !!active;
		if (!stickerList.isLoaded() && active) {
			await loadStickersForMetadata();
		}
		if (active) {
			stickerList.setFilter('');
			mapModel.addEventListener('activatedNodesChanged', loadStickersForActivatedNodes);
			mapModel.getIdea().addEventListener('changed', loadStickersForOtherCategories);
			loadStickersForOtherCategories();
		} else {
			mapModel.removeEventListener('activatedNodesChanged', loadStickersForActivatedNodes);
			mapModel.getIdea().removeEventListener('changed', loadStickersForOtherCategories);
		}
	};
	self.getStickersForCategory = (category) => {
		if (!isActive) {
			throw new Error('not-active');
		}
		if (!category) {
			throw new Error('invalid-args');
		}
		if (stickersForOtherCategories && stickersForOtherCategories[category]) {
			return stickersForOtherCategories[category];
		}
		return stickerList.getStickersForCategory(category);
	};

	self.getMetadataCategories = () => {
		if (!isActive) {
			throw new Error('not-active');
		}
		return stickerList.getCategories();
	};

	stickerList.addEventListener('changed', () => self.dispatchEvent('stickers-for-metadata-loaded'));

	self.addStickerToActivatedNodes = sticker => {
		//console.log('addStickerToActivatedNodes', JSON.stringify(sticker));
		if (!sticker) {
			throw new Error('invalid-args');
		}
		if (!mapModel.getEditingEnabled() || !mapModel.getInputEnabled()) {
			return false;
		}
		const stickerDescriptor = toStickerDescriptor(sticker),
			contentAggregate = mapModel.getIdea();
		mapModel.applyToActivated(id => {
			const idea = contentAggregate.findSubIdeaById(id),
				currentStickers = (idea && idea.getAttr(STICKERS_ATTR_NAME)) || [];
			if (!idea || !mapEditorModel.canStyleIdea(idea) || currentStickers.includes(stickerDescriptor)) {
				return;
			}

			contentAggregate.updateAttr(id, STICKERS_ATTR_NAME, currentStickers.slice(0).concat([stickerDescriptor]));
		});
		return true;
	};

	self.removeStickerFromActivatedNodes = sticker => {
		//console.log('removeStickerFromActivatedNodes', JSON.stringify(sticker));
		if (!sticker) {
			throw new Error('invalid-args');
		}
		if (!mapModel.getEditingEnabled() || !mapModel.getInputEnabled()) {
			return false;
		}
		const stickerDescriptor = toStickerDescriptor(sticker),
			contentAggregate = mapModel.getIdea();
		mapModel.applyToActivated(id => {
			const idea = contentAggregate.findSubIdeaById(id),
				currentStickers = (idea && idea.getAttr(STICKERS_ATTR_NAME));
			if (!currentStickers || !mapEditorModel.canStyleIdea(idea) || !currentStickers.includes(stickerDescriptor)) {
				return;
			}
			contentAggregate.updateAttr(id, STICKERS_ATTR_NAME, (currentStickers.length > 1 && currentStickers.filter(sticker => sticker !== stickerDescriptor)) || false);
		});
		return true;
	};
	self.removeAllStickersFromActivatedNodes = () => {
		if (!mapModel.getEditingEnabled() || !mapModel.getInputEnabled()) {
			return false;
		}
		const contentAggregate = mapModel.getIdea();
		mapModel.applyToActivated(id => {
			const idea = contentAggregate.findSubIdeaById(id),
				currentStickers = (idea && idea.getAttr(STICKERS_ATTR_NAME));
			if (!currentStickers || !mapEditorModel.canStyleIdea(idea)) {
				return;
			}
			contentAggregate.updateAttr(id, STICKERS_ATTR_NAME, false);
		});
		return true;

	};
};
